/* Shared product UI for the portal: visual allergen/suitability tags + a
   centred product-detail "peek" modal (same info as the public Available-to-
   Order detail). Used by the Add-products drawer and the My Collection page. */
import { useEffect, useState } from 'react';
import { Ico } from './Icons';
import type { PortalProduct } from '@/portal/api/types';
import { SAMPLE_TIMELINE } from '@/portal/data/product';

const LOGO_FALLBACK = '/assets/didiers-logo.svg';

/** Sample-request availability + timeline pill. */
export function AvailabilityBadge({
  availability,
  variant = 'chip',
}: {
  availability?: PortalProduct['availability'];
  variant?: 'chip' | 'row';
}) {
  if (!availability) return null;
  const t = SAMPLE_TIMELINE[availability];
  if (variant === 'row') {
    return (
      <div className={'pl-avail-row ' + t.tone}>
        <span className="pl-avail-dot" aria-hidden="true" />
        <span className="pl-avail-txt">
          <b>{t.label}</b>
          <span className="pl-avail-detail">{t.detail}</span>
        </span>
      </div>
    );
  }
  return (
    <span className={'pl-avail-chip ' + t.tone} title={t.detail}>
      <span className="pl-avail-dot" aria-hidden="true" />
      {t.label}
    </span>
  );
}

/**
 * Product thumbnail image with a branded fallback.
 * When the photo 404s (no photo synced yet) the Didier logo is shown on a
 * warm off-white tile so the card looks intentional, not broken.
 */
export function ProductImage({
  src,
  alt,
  className,
  ...rest
}: React.ImgHTMLAttributes<HTMLImageElement> & { src: string; alt: string }) {
  const [failed, setFailed] = useState(false);
  if (failed) {
    return (
      <div className={'pl-img-fallback' + (className ? ' ' + className : '')} aria-label={alt}>
        <img src={LOGO_FALLBACK} alt="" aria-hidden="true" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={alt}
      className={className}
      onError={() => setFailed(true)}
      {...rest}
    />
  );
}

/* Visual allergen + suitability tags (shown on product cards). */
export function ProductTags({ allergens, suitability }: { allergens: string[]; suitability: string[] }) {
  if (allergens.length === 0 && suitability.length === 0) return null;
  return (
    <div className="pl-tag-stack">
      {suitability.length > 0 && (
        <div className="pl-tags">
          {suitability.map((s) => (
            <span className="pl-tag suit" key={s}>
              {s}
            </span>
          ))}
        </div>
      )}
      {allergens.length > 0 && (
        <div className="pl-tags">
          <span className="pl-tag-lead">Contains</span>
          {allergens.map((a) => (
            <span className="pl-tag allergen" key={a}>
              {a}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* Product detail peek — mirrors the public Available-to-Order detail. */
export function ProductPeek({
  product,
  added,
  onToggle,
  onClose,
}: {
  product: PortalProduct;
  added: boolean;
  onToggle?: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', h);
    return () => document.removeEventListener('keydown', h);
  }, [onClose]);

  const specs: Array<[string, string]> = [];
  if (product.code) specs.push(['Product code', product.code]);
  if (product.weight) specs.push(['Weight', product.weight]);
  if (product.packSize) specs.push(['Case size', product.packSize]);

  return (
    <>
      <div className="pl-peek-scrim" onClick={onClose} />
      <div className="pl-peek" role="dialog" aria-label={product.name}>
        <button className="pl-x pl-peek-close" onClick={onClose} aria-label="Close">
          <Ico.x width={20} height={20} />
        </button>
        <div className="pl-peek-img">
          <ProductImage src={product.imgHi || product.img} alt={product.name} />
        </div>
        <div className="pl-peek-body">
          {onToggle && (
            <button
              className={'pl-btn ' + (added ? 'danger solid' : 'primary')}
              style={{ marginBottom: 16, width: '100%' }}
              onClick={onToggle}
            >
              {added ? (
                <>
                  <Ico.x width={16} height={16} /> Remove from selection
                </>
              ) : (
                <>
                  <Ico.plus width={16} height={16} /> Add to selection
                </>
              )}
            </button>
          )}
          {product.rangeLabel && <div className="pl-peek-range">{product.rangeLabel}</div>}
          <h3>{product.name}</h3>
          {product.desc && <p className="pl-peek-desc">{product.desc}</p>}

          <AvailabilityBadge availability={product.availability} variant="row" />


          {specs.length > 0 && (
            <dl className="pl-peek-specs">
              {specs.map(([k, v]) => (
                <div className="row2" key={k}>
                  <span className="k">{k}</span>
                  <span className="v">{v}</span>
                </div>
              ))}
            </dl>
          )}

          {product.suitability.length > 0 && (
            <div className="pl-peek-block">
              <div className="pl-peek-label">Suitable for</div>
              <div className="pl-tags">
                {product.suitability.map((s) => (
                  <span className="pl-tag suit" key={s}>
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          <div className="pl-peek-block">
            <div className="pl-peek-label">Allergens</div>
            {product.allergens.length > 0 ? (
              <div className="pl-tags">
                {product.allergens.map((a) => (
                  <span className="pl-tag allergen" key={a}>
                    {a}
                  </span>
                ))}
              </div>
            ) : (
              <p className="pl-peek-none">No declared allergens.</p>
            )}
          </div>

          {product.ingredients && (
            <div className="pl-peek-block">
              <div className="pl-peek-label">Ingredients</div>
              <p className="pl-peek-ingredients" dangerouslySetInnerHTML={{ __html: product.ingredients }} />
            </div>
          )}
        </div>
      </div>
    </>
  );
}
